import { useEffect, useCallback, useRef, useState } from 'react';
import { useWebSocket } from '@/hooks/use-websocket';
import { logger } from '@/utils';

export interface AnalyticsEvents {
  'data-source-connected': {
    dataSourceId: string;
    name: string;
    type: string;
    timestamp: Date;
  };
  'data-source-disconnected': {
    dataSourceId: string;
    reason: string;
    timestamp: Date;
  };
  'data-source-error': {
    dataSourceId: string;
    error: string;
    details?: any;
    timestamp: Date;
  };
  'query-executed': {
    queryId: string;
    dataSourceId: string;
    duration: number;
    recordCount: number;
    timestamp: Date;
  };
  'query-error': {
    queryId: string;
    dataSourceId: string;
    error: string;
    timestamp: Date;
  };
  'schema-updated': {
    dataSourceId: string;
    schemaId: string;
    changes: Array<{
      type: 'added' | 'modified' | 'removed';
      table?: string;
      field?: string;
      description: string;
    }>;
    timestamp: Date;
  };
  'cube-deployed': {
    cubeId: string;
    dataSourceId: string;
    measures: number;
    dimensions: number;
    preAggregations: number;
    timestamp: Date;
  };
  'analytics-alert': {
    type: 'performance' | 'error' | 'warning' | 'info';
    title: string;
    message: string;
    dataSourceId?: string;
    severity: 'low' | 'medium' | 'high' | 'critical';
    timestamp: Date;
    actionRequired?: boolean;
  };
}

export interface AnalyticsMetrics {
  connectedDataSources: number;
  activeQueries: number;
  totalQueries: number;
  averageQueryTime: number;
  errorRate: number;
  lastUpdated: Date;
}

export interface AnalyticsWebSocketState {
  isConnected: boolean;
  metrics: AnalyticsMetrics;
  alerts: Array<AnalyticsEvents['analytics-alert']>;
  recentActivity: Array<{
    id: string;
    type: 'connection' | 'query' | 'schema' | 'deployment' | 'error';
    message: string;
    timestamp: Date;
    severity?: 'low' | 'medium' | 'high' | 'critical';
  }>;
}

interface UseAnalyticsWebSocketOptions {
  autoConnect?: boolean;
  retryAttempts?: number;
  retryDelay?: number;
  maxAlerts?: number;
  maxRecentActivity?: number;
}

type EventHandlers = {
  [K in keyof AnalyticsEvents]?: (data: AnalyticsEvents[K]) => void;
};

export const useAnalyticsWebSocket = (
  options: UseAnalyticsWebSocketOptions = {}
) => {
  const {
    autoConnect = true,
    retryAttempts = 3,
    retryDelay = 1000,
    maxAlerts = 50,
    maxRecentActivity = 100
  } = options;

  const [state, setState] = useState<AnalyticsWebSocketState>({
    isConnected: false,
    metrics: {
      connectedDataSources: 0,
      activeQueries: 0,
      totalQueries: 0,
      averageQueryTime: 0,
      errorRate: 0,
      lastUpdated: new Date()
    },
    alerts: [],
    recentActivity: []
  });

  const eventHandlersRef = useRef<EventHandlers>({});
  const metricsIntervalRef = useRef<NodeJS.Timeout>();

  const { socket, isConnected, connect, disconnect, emit } = useWebSocket({
    namespace: '/analytics',
    autoConnect,
    retryAttempts,
    retryDelay
  });

  // Update connection state
  useEffect(() => {
    setState(prev => ({ ...prev, isConnected }));
  }, [isConnected]);

  // Request initial metrics when connected
  useEffect(() => {
    if (socket && isConnected) {
      emit('get-analytics-metrics');

      // Set up periodic metrics updates
      metricsIntervalRef.current = setInterval(() => {
        emit('get-analytics-metrics');
      }, 30000); // Update every 30 seconds
    } else if (metricsIntervalRef.current) {
      clearInterval(metricsIntervalRef.current);
    }

    return () => {
      if (metricsIntervalRef.current) {
        clearInterval(metricsIntervalRef.current);
      }
    };
  }, [socket, isConnected, emit]);

  // Set up event listeners
  useEffect(() => {
    if (!socket) return;

    const addActivity = (type: string, message: string, severity?: string) => {
      setState(prev => {
        const newActivity = {
          id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: type as any,
          message,
          timestamp: new Date(),
          severity: severity as any
        };

        return {
          ...prev,
          recentActivity: [newActivity, ...prev.recentActivity].slice(0, maxRecentActivity)
        };
      });
    };

    const addAlert = (alert: AnalyticsEvents['analytics-alert']) => {
      setState(prev => ({
        ...prev,
        alerts: [alert, ...prev.alerts].slice(0, maxAlerts)
      }));
    };

    const handleDataSourceConnected = (data: AnalyticsEvents['data-source-connected']) => {
      logger.info('Data source connected', data);
      addActivity('connection', `Connected to ${data.name} (${data.type})`, 'low');

      setState(prev => ({
        ...prev,
        metrics: {
          ...prev.metrics,
          connectedDataSources: prev.metrics.connectedDataSources + 1,
          lastUpdated: new Date()
        }
      }));

      eventHandlersRef.current['data-source-connected']?.(data);
    };

    const handleDataSourceDisconnected = (data: AnalyticsEvents['data-source-disconnected']) => {
      logger.info('Data source disconnected', data);
      addActivity('connection', `Disconnected from data source: ${data.reason}`, 'medium');

      setState(prev => ({
        ...prev,
        metrics: {
          ...prev.metrics,
          connectedDataSources: Math.max(0, prev.metrics.connectedDataSources - 1),
          lastUpdated: new Date()
        }
      }));

      eventHandlersRef.current['data-source-disconnected']?.(data);
    };

    const handleDataSourceError = (data: AnalyticsEvents['data-source-error']) => {
      logger.error('Data source error', data);
      addActivity('error', `Data source error: ${data.error}`, 'high');

      addAlert({
        type: 'error',
        title: 'Data Source Error',
        message: data.error,
        dataSourceId: data.dataSourceId,
        severity: 'high',
        timestamp: data.timestamp,
        actionRequired: true
      });

      eventHandlersRef.current['data-source-error']?.(data);
    };

    const handleQueryExecuted = (data: AnalyticsEvents['query-executed']) => {
      logger.info('Query executed', data);
      addActivity('query', `Query executed in ${data.duration}ms (${data.recordCount} records)`, 'low');

      setState(prev => ({
        ...prev,
        metrics: {
          ...prev.metrics,
          totalQueries: prev.metrics.totalQueries + 1,
          averageQueryTime: (prev.metrics.averageQueryTime + data.duration) / 2,
          lastUpdated: new Date()
        }
      }));

      eventHandlersRef.current['query-executed']?.(data);
    };

    const handleQueryError = (data: AnalyticsEvents['query-error']) => {
      logger.error('Query error', data);
      addActivity('error', `Query failed: ${data.error}`, 'medium');

      setState(prev => ({
        ...prev,
        metrics: {
          ...prev.metrics,
          errorRate: (prev.metrics.errorRate + 1) / (prev.metrics.totalQueries + 1),
          lastUpdated: new Date()
        }
      }));

      eventHandlersRef.current['query-error']?.(data);
    };

    const handleSchemaUpdated = (data: AnalyticsEvents['schema-updated']) => {
      logger.info('Schema updated', data);
      addActivity('schema', `Schema updated: ${data.changes.length} changes`, 'low');

      eventHandlersRef.current['schema-updated']?.(data);
    };

    const handleCubeDeployed = (data: AnalyticsEvents['cube-deployed']) => {
      logger.info('Cube deployed', data);
      addActivity('deployment', `Cube deployed: ${data.measures} measures, ${data.dimensions} dimensions`, 'low');

      eventHandlersRef.current['cube-deployed']?.(data);
    };

    const handleAnalyticsAlert = (data: AnalyticsEvents['analytics-alert']) => {
      logger.warn('Analytics alert', data);
      addAlert(data);

      eventHandlersRef.current['analytics-alert']?.(data);
    };

    const handleMetricsUpdate = (metrics: AnalyticsMetrics) => {
      setState(prev => ({
        ...prev,
        metrics: {
          ...metrics,
          lastUpdated: new Date()
        }
      }));
    };

    // Register event listeners
    socket.on('data-source-connected', handleDataSourceConnected);
    socket.on('data-source-disconnected', handleDataSourceDisconnected);
    socket.on('data-source-error', handleDataSourceError);
    socket.on('query-executed', handleQueryExecuted);
    socket.on('query-error', handleQueryError);
    socket.on('schema-updated', handleSchemaUpdated);
    socket.on('cube-deployed', handleCubeDeployed);
    socket.on('analytics-alert', handleAnalyticsAlert);
    socket.on('analytics-metrics', handleMetricsUpdate);

    return () => {
      socket.off('data-source-connected', handleDataSourceConnected);
      socket.off('data-source-disconnected', handleDataSourceDisconnected);
      socket.off('data-source-error', handleDataSourceError);
      socket.off('query-executed', handleQueryExecuted);
      socket.off('query-error', handleQueryError);
      socket.off('schema-updated', handleSchemaUpdated);
      socket.off('cube-deployed', handleCubeDeployed);
      socket.off('analytics-alert', handleAnalyticsAlert);
      socket.off('analytics-metrics', handleMetricsUpdate);
    };
  }, [socket, maxAlerts, maxRecentActivity]);

  // Event handler registration
  const on = useCallback(<K extends keyof AnalyticsEvents>(
    event: K,
    handler: (data: AnalyticsEvents[K]) => void
  ) => {
    eventHandlersRef.current[event] = handler;
  }, []);

  const off = useCallback(<K extends keyof AnalyticsEvents>(event: K) => {
    delete eventHandlersRef.current[event];
  }, []);

  // Alert management
  const dismissAlert = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      alerts: prev.alerts.filter((_, i) => i !== index)
    }));
  }, []);

  const clearAllAlerts = useCallback(() => {
    setState(prev => ({
      ...prev,
      alerts: []
    }));
  }, []);

  const markAlertAsRead = useCallback((index: number) => {
    setState(prev => ({
      ...prev,
      alerts: prev.alerts.map((alert, i) =>
        i === index ? { ...alert, actionRequired: false } : alert
      )
    }));
  }, []);

  // Activity management
  const clearRecentActivity = useCallback(() => {
    setState(prev => ({
      ...prev,
      recentActivity: []
    }));
  }, []);

  // Data source management
  const testDataSourceConnection = useCallback((dataSourceId: string) => {
    if (!socket || !isConnected) {
      logger.error('Cannot test connection: WebSocket not connected');
      return;
    }

    emit('test-data-source-connection', { dataSourceId });
  }, [socket, isConnected, emit]);

  const refreshDataSourceSchema = useCallback((dataSourceId: string) => {
    if (!socket || !isConnected) {
      logger.error('Cannot refresh schema: WebSocket not connected');
      return;
    }

    emit('refresh-data-source-schema', { dataSourceId });
  }, [socket, isConnected, emit]);

  // Metrics and health
  const requestMetricsUpdate = useCallback(() => {
    if (!socket || !isConnected) {
      return;
    }

    emit('get-analytics-metrics');
  }, [socket, isConnected, emit]);

  const getHealthStatus = useCallback(() => {
    const { metrics } = state;

    if (!isConnected) return 'disconnected';
    if (metrics.errorRate > 0.1) return 'unhealthy'; // >10% error rate
    if (metrics.connectedDataSources === 0) return 'warning';
    if (metrics.averageQueryTime > 5000) return 'warning'; // >5s avg query time

    return 'healthy';
  }, [state, isConnected]);

  const getCriticalAlerts = useCallback(() => {
    return state.alerts.filter(alert =>
      alert.severity === 'critical' && alert.actionRequired
    );
  }, [state.alerts]);

  return {
    // State
    ...state,
    healthStatus: getHealthStatus(),
    criticalAlerts: getCriticalAlerts(),
    unreadAlerts: state.alerts.filter(alert => alert.actionRequired).length,

    // Actions
    connect,
    disconnect,
    testDataSourceConnection,
    refreshDataSourceSchema,
    requestMetricsUpdate,

    // Alert management
    dismissAlert,
    clearAllAlerts,
    markAlertAsRead,

    // Activity management
    clearRecentActivity,

    // Event handlers
    on,
    off,

    // Utilities
    isHealthy: getHealthStatus() === 'healthy',
    hasWarnings: getHealthStatus() === 'warning',
    hasErrors: getHealthStatus() === 'unhealthy' || getHealthStatus() === 'disconnected'
  };
};